/**
 * Comidas habituales (0.3.5): crearlas al confirmarlas y registrar lo que se
 * comió al confirmarlo.
 *
 * - Se guardan en los ajustes (Settings.comidasHabituales): no hace falta otra
 *   tabla ni cambiar el esquema. Viajan en los respaldos con los ajustes.
 * - Crear solo agrega: los alimentos que falten en Mis alimentos y las
 *   comidas que falten. Un alimento que ya está (por su id fijo, o uno igual
 *   que ya habías capturado) se usa sin modificarlo. Repetirlo no duplica.
 * - Registrar escribe todos los alimentos confirmados en una sola
 *   transacción, con ids creados al abrir la pantalla: tocar dos veces no
 *   duplica, y la misma comida no se registra dos veces el mismo día.
 * - Nada se registra solo, ni hacia atrás.
 */

import { db, asegurarSettings, leerSettings } from './schema';
import { enEscritura } from './tx';
import { validarAlimento } from './alimentos';
import { validarComida } from './comidas';
import type { ComidaHabitual, Food, MealEntry, PersonaRutinas, Settings } from './types';
import { ahoraIso, esFechaLocal, hoy as hoyLocal, type FechaLocal } from '@/domain/dates';
import { instanteLocal } from '@/domain/time';
import { normalizar } from '@/domain/busqueda';
import { ErrorValidacion } from '@/domain/errors';
import {
  ALIMENTOS_HABITUALES_DE_JANETH,
  COMIDAS_HABITUALES_DE_JANETH,
  type AlimentoDePlantilla,
  type ComidaDePlantilla,
} from '@/datos/comidas-janeth';

export interface PlantillaDeHabituales {
  persona: PersonaRutinas;
  alimentos: readonly AlimentoDePlantilla[];
  comidas: readonly ComidaDePlantilla[];
}

/** Solo Janeth mandó las suyas (26 oct 2026). */
export const PLANTILLAS_HABITUALES: Readonly<Partial<Record<PersonaRutinas, PlantillaDeHabituales>>> = {
  janeth: { persona: 'janeth', alimentos: ALIMENTOS_HABITUALES_DE_JANETH, comidas: COMIDAS_HABITUALES_DE_JANETH },
};

export function plantillaHabitualDe(persona: PersonaRutinas | null | undefined): PlantillaDeHabituales | null {
  return persona === 'janeth' || persona === 'fernando' ? (PLANTILLAS_HABITUALES[persona] ?? null) : null;
}

/** Ausente (ajustes de antes de la 0.3.5) = ninguna. */
export function habitualesDe(ajustes: Pick<Settings, 'comidasHabituales'> | undefined): ComidaHabitual[] {
  return Array.isArray(ajustes?.comidasHabituales) ? ajustes.comidasHabituales : [];
}

/** LECTURA PURA. */
export async function leerComidasHabituales(): Promise<ComidaHabitual[]> {
  return habitualesDe(await leerSettings());
}

/** Las de la plantilla que todavía no están, por su id fijo. */
export function comidasQueFaltan(
  existentes: readonly Pick<ComidaHabitual, 'id'>[],
  plantilla: readonly ComidaDePlantilla[],
): ComidaDePlantilla[] {
  return plantilla.filter((c) => !existentes.some((h) => h.id === c.id));
}

// ---------------------------------------------------------------------------
// Alimentos de la plantilla contra Mis alimentos
// ---------------------------------------------------------------------------

const igualNumero = (a: number | null, b: number | null) =>
  a === null || b === null ? a === b : Math.abs(a - b) <= 1e-9 * Math.max(1, Math.abs(a), Math.abs(b));

/**
 * Se puede usar en lugar del de la plantilla: mismo nombre (sin acentos ni
 * mayúsculas), misma calidad del dato, mismo estado, misma referencia y
 * mismos valores. La porción de la etiqueta se compara tal cual, porque las
 * comidas habituales la usan por su nombre ("scoop" no es "Scoop").
 */
export function mismoAlimento(f: Food, p: AlimentoDePlantilla): boolean {
  if (normalizar(f.nombre) !== normalizar(p.nombre)) return false;
  if ((f.calidad ?? null) !== p.calidad) return false;
  if ((f.estado ?? null) !== (p.estado ?? null)) return false;
  if (f.referencia.tipo !== p.referencia.tipo) return false;
  if (f.referencia.tipo === 'porPorcion' && p.referencia.tipo === 'porPorcion') {
    const a = f.referencia.porcion;
    const b = p.referencia.porcion;
    if (a.nombre.trim() !== b.nombre.trim() || !igualNumero(a.gramos, b.gramos)) return false;
  }
  const campos = ['kcal', 'proteinaG', 'carbohidratosG', 'grasaG', 'fibraG'] as const;
  return campos.every((c) => igualNumero(f.nutrientes[c], p.nutrientes[c]));
}

export interface RevisionDeAlimentos {
  /** Ya están con su id fijo: se usan sin tocarlos. */
  yaEstan: Set<string>;
  /** Id de la plantilla → el de un alimento igual que ya estaba. */
  reutilizar: Map<string, string>;
  /** Hay uno con el mismo nombre pero no igual: se agrega el de la plantilla y el tuyo no cambia. */
  conOtrosValores: Array<{ plantilla: AlimentoDePlantilla; existente: Food }>;
}

/** Las comidas de la plantilla con los alimentos que se van a usar: los que ya estaban, si son iguales. */
export function comidasConAlimentosDe(comidas: readonly ComidaDePlantilla[], revision: RevisionDeAlimentos): ComidaDePlantilla[] {
  return comidas.map((c) => ({
    ...c,
    items: c.items.map((i) => ({
      ...i,
      alimentoId: i.origen === 'propio' ? (revision.reutilizar.get(i.alimentoId) ?? i.alimentoId) : i.alimentoId,
    })),
  }));
}

/** Pura: qué pasa con cada alimento de la plantilla al crear. */
export function revisarAlimentos(plantilla: readonly AlimentoDePlantilla[], propios: readonly Food[]): RevisionDeAlimentos {
  const revision: RevisionDeAlimentos = { yaEstan: new Set(), reutilizar: new Map(), conOtrosValores: [] };
  // Los activos primero: si hay dos iguales, se usa el que aparece al buscar
  const ordenados = [...propios].sort((a, b) => Number(a.archivado) - Number(b.archivado));
  for (const p of plantilla) {
    if (propios.some((f) => f.id === p.id)) {
      revision.yaEstan.add(p.id);
      continue;
    }
    const igual = ordenados.find((f) => mismoAlimento(f, p));
    if (igual !== undefined) {
      revision.reutilizar.set(p.id, igual.id);
      continue;
    }
    const mismoNombre = ordenados.find((f) => normalizar(f.nombre) === normalizar(p.nombre));
    if (mismoNombre !== undefined) revision.conOtrosValores.push({ plantilla: p, existente: mismoNombre });
  }
  return revision;
}

// ---------------------------------------------------------------------------
// Crear
// ---------------------------------------------------------------------------

export interface ResultadoDeCrear {
  creadas: string[];
  /** Alimentos agregados a Mis alimentos. */
  agregados: string[];
  /** Alimentos que ya estaban y se usaron. */
  reutilizados: string[];
}

/**
 * Crea las comidas habituales que falten de esa persona, ya revisadas, en una
 * sola transacción: primero sus alimentos en Mis alimentos y después las
 * comidas, desde hoy. Solo agrega; no cambia ni borra nada de lo que ya está
 * y no registra nada. ESCRIBE.
 */
export async function crearComidasHabituales(
  persona: PersonaRutinas,
  hoy: FechaLocal,
  ahora: string = ahoraIso(),
): Promise<ResultadoDeCrear> {
  const plantilla = plantillaHabitualDe(persona);
  if (plantilla === null) throw new ErrorValidacion('Todavía no hay comidas habituales para este teléfono.');
  if (!esFechaLocal(hoy)) throw new ErrorValidacion('La fecha no es válida.');
  await asegurarSettings();
  return enEscritura([db.foods, db.settings], async () => {
    const existentes = habitualesDe(await db.settings.get(1));
    const faltan = comidasQueFaltan(existentes, plantilla.comidas);
    const resultado: ResultadoDeCrear = { creadas: [], agregados: [], reutilizados: [] };
    if (faltan.length === 0) return resultado;

    const revision = revisarAlimentos(plantilla.alimentos, await db.foods.toArray());
    const usados = new Set(faltan.flatMap((c) => c.items.filter((i) => i.origen === 'propio').map((i) => i.alimentoId)));
    const ids = new Map<string, string>();
    for (const a of plantilla.alimentos) {
      if (!usados.has(a.id)) continue;
      const otro = revision.yaEstan.has(a.id) ? a.id : revision.reutilizar.get(a.id);
      if (otro !== undefined) {
        ids.set(a.id, otro);
        resultado.reutilizados.push(otro);
        continue;
      }
      const nuevo: Food = { ...a, porciones: a.porciones.map((p) => ({ ...p })), favorito: false, archivado: false, creadoEn: ahora, actualizadoEn: ahora };
      validarAlimento(nuevo);
      await db.foods.add(nuevo);
      ids.set(a.id, a.id);
      resultado.agregados.push(a.id);
    }

    const nuevas: ComidaHabitual[] = faltan.map((c) => ({
      id: c.id,
      nombre: c.nombre,
      tiempo: c.tiempo,
      dias: [...c.dias],
      items: c.items.map((i) => ({ ...i, alimentoId: i.origen === 'propio' ? (ids.get(i.alimentoId) ?? i.alimentoId) : i.alimentoId })),
      desde: hoy,
      creadoEn: ahora,
      actualizadoEn: ahora,
    }));
    await db.settings.update(1, { comidasHabituales: [...existentes, ...nuevas] });
    resultado.creadas = nuevas.map((n) => n.id);
    return resultado;
  });
}

// ---------------------------------------------------------------------------
// Registrar lo que se comió
// ---------------------------------------------------------------------------

export const CANTIDAD_MAXIMA_HABITUAL = 20_000;

/** La cantidad y la porción de cada alimento, en el orden de la comida habitual. */
export type CantidadesHabituales = ReadonlyArray<{ cantidad: number; porcion: string }>;

function validarCantidades(cantidades: CantidadesHabituales): void {
  for (const c of cantidades) {
    if (!(Number.isFinite(c.cantidad) && c.cantidad > 0 && c.cantidad <= CANTIDAD_MAXIMA_HABITUAL)) {
      throw new ErrorValidacion('Cada cantidad debe ser mayor que cero.');
    }
    if (c.porcion.trim() === '') throw new ErrorValidacion('Elige la porción de cada alimento.');
  }
}

export interface OpcionesDeRegistro {
  /** También quedan como las nuevas cantidades de la comida habitual. */
  cantidades?: CantidadesHabituales | null;
  /** La fecha de hoy; un día posterior no se registra. */
  hoy?: FechaLocal;
}

/**
 * Registra lo que se confirmó de una comida habitual en un día (entradas de
 * domain/habituales.ts, entradasDeHabitual), en una sola transacción. Nunca
 * por adelantado. Si ese día ya se registró con otras entradas, no hace nada
 * y lo dice. Con `cantidades`, también quedan como las nuevas de la comida
 * habitual; lo ya registrado otros días no cambia. ESCRIBE.
 */
export async function registrarHabitual(
  habitualId: string,
  entradas: readonly MealEntry[],
  { cantidades = null, hoy = hoyLocal() }: OpcionesDeRegistro = {},
): Promise<MealEntry[]> {
  const primera = entradas[0];
  if (primera === undefined) throw new ErrorValidacion('Marca al menos un alimento que comiste.');
  for (const e of entradas) {
    validarComida(e);
    if (e.habitual !== habitualId || e.fecha !== primera.fecha || e.tiempo !== primera.tiempo) {
      throw new ErrorValidacion('Vuelve a abrir esta pantalla.');
    }
  }
  if (primera.fecha > hoy) throw new ErrorValidacion('Se registra cuando lo comas, nunca por adelantado.');
  if (cantidades !== null) validarCantidades(cantidades);
  const ahora = ahoraIso();
  const registros = entradas.map((e) => ({ ...e, momento: instanteLocal(e.fecha, e.hora), actualizadoEn: ahora }));
  await asegurarSettings();
  return enEscritura([db.mealEntries, db.settings], async () => {
    const habituales = habitualesDe(await db.settings.get(1));
    const habitual = habituales.find((h) => h.id === habitualId);
    if (habitual === undefined) throw new ErrorValidacion('Esa comida habitual ya no existe.');
    const nuestros = new Set(registros.map((r) => r.id));
    const delDia = await db.mealEntries.where('fecha').equals(primera.fecha).toArray();
    if (delDia.some((e) => e.habitual === habitualId && !nuestros.has(e.id))) {
      throw new ErrorValidacion(`Ya registraste tu ${habitual.nombre.toLocaleLowerCase('es-MX')} ese día.`);
    }
    await db.mealEntries.bulkPut(registros);
    if (cantidades !== null) {
      if (cantidades.length !== habitual.items.length) throw new ErrorValidacion('Vuelve a abrir esta pantalla.');
      const actualizada: ComidaHabitual = {
        ...habitual,
        items: habitual.items.map((i, n) => ({ ...i, cantidad: cantidades[n]?.cantidad ?? i.cantidad, porcion: cantidades[n]?.porcion ?? i.porcion })),
        actualizadoEn: ahora,
      };
      await db.settings.update(1, { comidasHabituales: habituales.map((h) => (h.id === habitualId ? actualizada : h)) });
    }
    return registros;
  });
}

/** ¿Algún alimento propio está en una comida habitual? Entonces solo se archiva. */
export function usadoEnHabituales(habituales: readonly ComidaHabitual[], foodId: string): boolean {
  return habituales.some((h) => h.items.some((i) => i.origen === 'propio' && i.alimentoId === foodId));
}
